import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AiService } from '../../../services/ai.service';
import { AuthService } from 'src/app/_services/auth.service';
import { AI_ASSISTANT_NAME } from 'src/app/_core/config/app-config.const';
import { Router } from '@angular/router';
import { unlockPremiumComponent } from '../../unlockPremium/unlockPremium.component';

/**
 * "Create Tangram from Pasted Text" entry point. Owns its own idle UI — a
 * rich-text editor with a small formatting toolbar (B/I/U, lists, link,
 * remove-format) — so the pasted-text flow has a dedicated layout distinct
 * from the file-upload dropzone, while the extraction pipeline itself is
 * reused by serialising the editor content into a plain-text `File` and
 * submitting it through the existing `AiService.extractDocument` endpoint.
 * Reusing that endpoint means the backend analysis, the clarification/
 * finalize pipeline, and the legacy response shape all stay unchanged — no
 * new server-side route is required for this flow.
 */

// Once extraction succeeds we flip to 'handoff' and render the upload
// component with the pre-extracted response, reusing its clarification,
// extracted, decision, mediaSetup, grouping, creating, and done screens
// without duplicating any state-machine code.
type UiState = 'idle' | 'extracting' | 'handoff';

// Soft cap so the editor doesn't accumulate so much content that the serialized
// Blob becomes awkward. The backend separately truncates at 12k for analysis.
const MAX_PASTED_CHARS = 20000;

@Component({
  selector: 'tangram-create-from-text',
  templateUrl: './create-from-text.component.html',
  styleUrls: ['./create-from-text.component.scss'],
})
export class CreateFromTextComponent implements OnDestroy, AfterViewInit {
  @ViewChild('richTextEditor', { static: false }) richTextEditor: ElementRef<HTMLElement>;
  @Output() draftCreated = new EventEmitter<{ pathwayId: string; tangramTitle: string; mediaCount: number }>();
  /** Text typed/pasted in the parent universal input. When set, the editor is
   *  pre-populated so the user can go straight to "Generate Tangram using AI". */
  @Input() preloadedText: string = '';

  state: UiState = 'idle';
  errorMessage = '';

  // True when the most recent extraction finished but produced no usable
  // sections — either the backend rejected the text ("No extractable
  // links or media…") or the analyser returned `sections: []`. Swaps the
  // inline error banner for a friendlier empty-state card.
  extractionEmpty = false;

  // Rich-text editor state. `pastedHtml` preserves the full markup (bold,
  // italic, underline, lists, hyperlinks) so the editor can re-render it if
  // the user navigates away and back; `pastedPlainText` is the normalised
  // text we actually submit to the AI — HTML stripped, hyperlinks inlined as
  // "label (https://…)" so the analyser sees every URL even when the visible
  // label was something like "click here".
  pastedHtml = '';
  pastedPlainText = '';
  pastedCharCount = 0;
  // URLs scraped from the editor on every edit. Surfaced in the UI as a small
  // badge so the user can see the extractor found them, and appended verbatim
  // to the AI payload as a canonical "Detected Links:" block so URLs are never
  // silently lost (anchors with non-http hrefs, schemeless "example.com" text,
  // URLs buried in tables, etc.).
  detectedUrls: string[] = [];

  // Optional guidance — same semantics as the upload flow's userComment.
  userComment = '';

  // Which provider was used for the most recent extraction. Drives the
  // "analyzing with …" label on the two provider buttons.
  // Every user-facing label goes through this — the UI never names the
  // underlying model. See AI_ASSISTANT_NAME in app-config.const.ts.
  readonly aiName = AI_ASSISTANT_NAME;

  providerLoading: 'gemini' | 'claude' | null = null;

  // The raw HTTP-envelope response from extractDocument. Passed verbatim to
  // the embedded <tangram-create-from-upload> via its `preExtractedResponse`
  // input so the full clarification / extracted / decision / mediaSetup /
  // grouping / creating / done pipeline is reused without duplicating state.
  extractionResponse: any = null;
  extractionProvider: 'gemini' | 'claude' | null = null;

  /** Reuse stored parse (text + structure) when only the comment or provider changes. */
  private pendingDocumentStructureId = '';
  private lastAnalyzedContentKey = '';

  private currentUser: any = null;
  private isUserLoggedIn = false;
  private userSub: Subscription;

  readonly maxPastedChars = MAX_PASTED_CHARS;

  constructor(
    private aiService: AiService,
    private toaster: ToastrService,
    private authService: AuthService,
    private router: Router,
    private modalService: NgbModal,
  ) {
    this.userSub = this.authService.currentUser.subscribe((u) => {
      this.currentUser = u;
      this.isUserLoggedIn = !!u;
    });
  }

  ngAfterViewInit() {
    if (this.preloadedText && this.richTextEditor && this.richTextEditor.nativeElement) {
      this.richTextEditor.nativeElement.innerText = this.preloadedText;
      this.onRichTextInput();
    }
  }

  ngOnDestroy() {
    if (this.userSub) { this.userSub.unsubscribe(); }
  }

  // ── Rich-text editor handlers ───────────────────────────────────
  /**
   * Apply a whitelisted formatting command via document.execCommand. The API
   * is deprecated but still universally supported and is the lightest way to
   * add B/I/U/list/remove-format without pulling in a third-party RTE library.
   * The allowed values are a closed enum — user input never reaches the
   * command argument.
   */
  applyRichTextCommand(command: 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList' | 'removeFormat') {
    if (!this.richTextEditor || !this.richTextEditor.nativeElement) { return; }
    this.richTextEditor.nativeElement.focus();
    try {
      document.execCommand(command, false);
    } catch (_) { /* execCommand rarely throws on unfocused iframes */ }
    this.syncEditorState();
  }

  /** Prompt for a URL and wrap the current selection as a hyperlink. */
  applyRichTextLink() {
    if (!this.richTextEditor || !this.richTextEditor.nativeElement) { return; }
    this.richTextEditor.nativeElement.focus();
    const url = (window.prompt('Enter the URL (including https://):', 'https://') || '').trim();
    if (!url) { return; }
    try {
      document.execCommand('createLink', false, url);
    } catch (_) { /* no-op */ }
    this.syncEditorState();
  }

  onRichTextInput() {
    this.syncEditorState();
    // Typing after a "no content" hit should clear the empty-state banner so
    // the user isn't staring at stale feedback while they add URLs.
    if (this.extractionEmpty) { this.extractionEmpty = false; }
  }

  /**
   * Let the browser handle the actual paste so hyperlinks, lists, and basic
   * formatting from Google Docs / Word arrive as rich HTML (the whole point
   * of accepting rich text). We just recompute the plain-text view after the
   * paste lands.
   */
  onRichTextPaste(_event: ClipboardEvent) {
    setTimeout(() => this.syncEditorState(), 0);
  }

  /** Clear the editor and all derived state. */
  clearEditor() {
    this.pastedHtml = '';
    this.pastedPlainText = '';
    this.pastedCharCount = 0;
    this.detectedUrls = [];
    this.errorMessage = '';
    this.extractionEmpty = false;
    this.userComment = '';
    this.state = 'idle';
    this.extractionResponse = null;
    this.extractionProvider = null;
    this.pendingDocumentStructureId = '';
    this.lastAnalyzedContentKey = '';
    if (this.richTextEditor && this.richTextEditor.nativeElement) {
      this.richTextEditor.nativeElement.innerHTML = '';
    }
  }

  private syncEditorState() {
    const el = this.richTextEditor && this.richTextEditor.nativeElement;
    if (!el) { return; }
    this.pastedHtml = el.innerHTML || '';
    this.pastedPlainText = this.domToAnnotatedText(el);
    this.pastedCharCount = this.pastedPlainText.trim().length;
    this.detectedUrls = this.collectUrls(el, this.pastedPlainText);
    if (this.errorMessage) { this.errorMessage = ''; }
    if (this.lastAnalyzedContentKey) {
      const key = this.getContentKeyForCurrentEditor();
      if (key !== this.lastAnalyzedContentKey) {
        this.pendingDocumentStructureId = '';
      }
    }
  }

  /**
   * Scrape every URL out of the editor — anchor hrefs first (covers anchors
   * whose visible label is "click here" or which `domToAnnotatedText` skipped
   * because the href wasn't http/https at walk time), then a regex pass over
   * the annotated plain text. Results are normalised (scheme added for bare
   * domains, trailing punctuation stripped) and de-duped while preserving
   * first-seen order.
   */
  private collectUrls(root: HTMLElement, plainText: string): string[] {
    const found: string[] = [];
    const seen = new Set<string>();
    const push = (raw: string) => {
      const normalised = this.normaliseUrl(raw);
      if (!normalised || seen.has(normalised)) { return; }
      seen.add(normalised);
      found.push(normalised);
    };

    const anchors = root.querySelectorAll('a[href]');
    for (let i = 0; i < anchors.length; i++) {
      push(anchors[i].getAttribute('href') || '');
    }

    // Schemeless matches (bare domains like "example.com/foo") are caught by
    // the second alternation; the normaliser adds https:// if the scheme is
    // missing. The character class at the end excludes punctuation that is
    // almost always sentence-terminating, not part of the URL.
    const urlRegex = /\b((?:https?:\/\/|www\.)[^\s<>"'()]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"'()]*)?)/gi;
    let match: RegExpExecArray | null;
    while ((match = urlRegex.exec(plainText)) !== null) {
      push(match[1]);
    }

    return found;
  }

  /**
   * Trim quotes/punctuation the regex commonly grabs, add a scheme when the
   * user typed a bare domain, and reject anything that clearly isn't an HTTP
   * link (mailto:, tel:, javascript:, file paths, anchors within the page).
   */
  private normaliseUrl(raw: string): string {
    if (!raw) { return ''; }
    let url = raw.trim().replace(/[),.;:!?]+$/g, '');
    if (!url) { return ''; }
    if (/^(mailto:|tel:|javascript:|#)/i.test(url)) { return ''; }
    if (!/^https?:\/\//i.test(url)) {
      if (/^www\./i.test(url) || /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/|$)/i.test(url)) {
        url = 'https://' + url;
      } else {
        return '';
      }
    }
    try {
      // Round-trip through the URL parser to reject malformed strings and
      // canonicalise (drops hash fragments isn't required; keep as-is).
      const u = new URL(url);
      if (!u.hostname || !u.hostname.includes('.')) { return ''; }
      return u.toString();
    } catch (_) {
      return '';
    }
  }

  /**
   * Serialize the editor subtree to plain text while preserving every
   * `<a href="…">label</a>` as "label (url)" so the analyser sees every
   * destination even when the visible label was "click here". Block-level
   * elements contribute line breaks; list items get a "- " prefix.
   */
  private domToAnnotatedText(root: Node): string {
    const BLOCK_TAGS = new Set([
      'P', 'DIV', 'BR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TR',
    ]);
    const parts: string[] = [];
    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push((node.textContent || '').replace(/ /g, ' '));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) { return; }
      const el = node as HTMLElement;
      const tag = el.tagName;

      if (tag === 'BR') { parts.push('\n'); return; }
      if (tag === 'A') {
        const text = (el.textContent || '').trim();
        const href = (el.getAttribute('href') || '').trim();
        if (href && href !== text && /^https?:\/\//i.test(href)) {
          parts.push(text ? `${text} (${href})` : href);
        } else {
          parts.push(text || href);
        }
        return;
      }

      if (tag === 'LI') { parts.push('\n- '); }
      else if (BLOCK_TAGS.has(tag)) { parts.push('\n'); }

      for (let i = 0; i < el.childNodes.length; i++) { walk(el.childNodes[i]); }

      if (BLOCK_TAGS.has(tag)) { parts.push('\n'); }
    };
    for (let i = 0; i < (root.childNodes || []).length; i++) { walk(root.childNodes[i]); }
    return parts.join('').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  // ── Extraction ──────────────────────────────────────────────────
  get canExtract(): boolean {
    return this.pastedCharCount > 0 && !this.providerLoading;
  }

  /**
   * Run the pasted text through the AI analysis pipeline. We reuse the
   * existing `/ai/extract-document` endpoint by packaging the serialised
   * plain text as a `text/plain` File — the backend already handles TXT
   * uploads via `documentService.extractDocumentText` and the rest of the
   * pipeline (user-comment/clarification branching, analysis, plan shape)
   * is identical regardless of how the text arrived.
   */
  extractWithAI(provider: 'gemini' | 'claude' = 'claude') {
    if (!this.canExtract) { return; }
    if (!this.isUserLoggedIn) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: '/create-tangram' } });
      return;
    }
    if (!this.currentUser || !this.currentUser.subscriptionActive) {
      this.modalService.open(unlockPremiumComponent, { centered: true, windowClass: 'unlock-premium' });
      return;
    }

    this.providerLoading = provider;
    this.errorMessage = '';
    this.extractionEmpty = false;
    this.state = 'extracting';

    // Append the canonical URL list so the analyser never misses a link that
    // was buried in prose, wrapped in a non-http anchor, or typed without a
    // scheme. The block is plain-text and human-readable — it reads naturally
    // in the raw document view and is trivial for the AI to parse.
    const contentKey = this.getContentKeyForCurrentEditor();
    const textForAnalysis = this.getTextForAnalysis();
    const reuseStoredStructure = !!(
      this.pendingDocumentStructureId
      && this.lastAnalyzedContentKey
      && contentKey === this.lastAnalyzedContentKey
    );

    const pseudoFile = reuseStoredStructure
      ? null
      : new File(
        [new Blob([textForAnalysis], { type: 'text/plain' })],
        'Pasted Text.txt',
        { type: 'text/plain' },
      );

    this.aiService.extractDocument(
      pseudoFile,
      provider,
      this.userComment,
      reuseStoredStructure ? this.pendingDocumentStructureId : undefined,
    ).subscribe(
      (response: any) => {
        this.providerLoading = null;
        if (!response || !response.success || !response.data) {
          const msg = (response && response.message) || 'Extraction failed. Please try again.';
          this.errorMessage = msg;
          this.extractionEmpty = CreateFromTextComponent.isEmptyContentMessage(msg);
          if (!this.extractionEmpty) { this.toaster.error(msg); }
          this.state = 'idle';
          return;
        }

        // Detect success-but-empty before handing off — the upload
        // component's idle view is suppressed in handoff mode, so we'd
        // never see its empty-state card fire if we forwarded an
        // empty-sections response.
        const stage = response.stage || (response.data && response.data.stage) || 'plan_ready';
        const sections = Array.isArray(response.data && response.data.sections) ? response.data.sections : [];
        if (stage === 'plan_ready' && sections.length === 0) {
          this.errorMessage = 'No links or media items could be created from the pasted text. Try pasting content with URLs, articles, videos, or images — or add a comment below telling the AI what you want.';
          this.extractionEmpty = true;
          this.state = 'idle';
          return;
        }

        const structureId = response.data && response.data.documentStructureId;
        if (structureId) {
          this.pendingDocumentStructureId = String(structureId);
          this.lastAnalyzedContentKey = contentKey;
        }

        // Hand the response off to the upload component, which owns the full
        // post-extraction state machine (clarification → finalizing →
        // extracted → decision → mediaSetup → grouping → creating → done).
        this.extractionResponse = response;
        this.extractionProvider = (response.provider || (response.data && response.data.provider) || provider) as 'gemini' | 'claude';
        this.state = 'handoff';
      },
      (err: any) => {
        this.providerLoading = null;
        const msg = (err && err.error && err.error.message) || 'Failed to extract from pasted text. Please try again.';
        this.errorMessage = msg;
        this.extractionEmpty = CreateFromTextComponent.isEmptyContentMessage(msg);
        if (!this.extractionEmpty) { this.toaster.error(msg); }
        this.state = 'idle';
      },
    );
  }

  /**
   * Same heuristic as the upload component — matches backend 4xx "no
   * extractable content" responses plus the client-side success-but-empty
   * fallback so the friendlier empty-state card fires in the same cases.
   */
  private getTextForAnalysis(): string {
    const urlsBlock = this.detectedUrls.length
      ? `\n\nDetected Links:\n${this.detectedUrls.map(u => `- ${u}`).join('\n')}`
      : '';
    return `${this.pastedPlainText}${urlsBlock}`;
  }

  private getContentKeyForCurrentEditor(): string {
    return this.buildContentKey(this.getTextForAnalysis());
  }

  /** Fingerprint of pasted body + detected URLs — must match to reuse structure. */
  private buildContentKey(textForAnalysis: string): string {
    const t = String(textForAnalysis || '');
    return `${t.length}:${t.slice(0, 500)}:${this.detectedUrls.join('|')}`;
  }

  private static isEmptyContentMessage(msg: string): boolean {
    if (!msg) { return false; }
    const m = msg.toLowerCase();
    return m.includes('no extractable')
      || m.includes('no links or media')
      || m.includes('no usable')
      || m.includes('could not identify any sections');
  }

  /**
   * Invoked by the embedded upload component when the user starts over from
   * within its post-extraction views. Snap back to the rich-text editor so
   * the user doesn't see the file dropzone flash in.
   */
  onHandoffReset() {
    this.extractionResponse = null;
    this.extractionProvider = null;
    this.state = 'idle';
  }

  /**
   * Forward the upload component's `draftCreated` event out through this
   * component's own output so callers higher up the tree see a consistent
   * event regardless of which entry point (file vs text) produced the draft.
   */
  onHandoffDraftCreated(event: { pathwayId: string; tangramTitle: string; mediaCount: number }) {
    this.draftCreated.emit(event);
  }
}
